/**
 * VideoCapture — camera input, start/stop recording.
 * Pure UI + WebRTC; no API calls here.
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import Button from '../../ui/Button.js';

// Choose a recording container/codec the CURRENT browser can actually record.
// Order matters: Chrome/Firefox/Android record webm natively; Safari — and
// therefore EVERY browser on iOS/iPadOS, since they're all WebKit — only
// supports mp4/H.264 and will THROW on webm. Falling through to mp4 last means
// Safari/iPad gets mp4 while everyone else keeps webm.
function pickRecordingType() {
  const candidates = [
    { mimeType: 'video/webm;codecs=vp9', ext: 'webm' },
    { mimeType: 'video/webm;codecs=vp8', ext: 'webm' },
    { mimeType: 'video/webm', ext: 'webm' },
    { mimeType: 'video/mp4', ext: 'mp4' }, // Safari / iOS / iPadOS
  ];
  const canCheck = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
  for (const c of candidates) {
    if (canCheck && MediaRecorder.isTypeSupported(c.mimeType)) return c;
  }
  return { mimeType: '', ext: 'webm' }; // let the browser pick its own default
}

export function VideoCapture({ onRecordingComplete, disabled = false }) {
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const [cameraActive, setCameraActive] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  // Default to the BACK camera — a coach films the athlete, not themselves.
  const [facingMode, setFacingMode] = useState('environment');
  const timerRef = useRef(null);

  const startCamera = useCallback(async (mode) => {
    const useMode = mode || 'environment';
    setError(null);
    // stop any existing stream first (needed when switching cameras)
    streamRef.current?.getTracks().forEach(t => t.stop());
    try {
      let stream;
      try {
        // `ideal` prefers these but won't hard-fail if the device can't match.
        // 720p@30 gives the pose estimator enough detail without huge files.
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: useMode },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 },
          },
          audio: false,
        });
      } catch {
        // some devices reject facingMode constraints — fall back to any camera
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // iOS can reject play() outside some gesture contexts; ignore — the
        // muted+playsInline live preview still renders.
        videoRef.current.play().catch(() => {});
      }
      setCameraActive(true);
    } catch (err) {
      setError(`Camera error: ${err.message}`);
    }
  }, []);

  // Flip between the back (environment) and front (user) camera.
  const switchCamera = useCallback(async () => {
    const next = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(next);
    if (cameraActive && !recording) await startCamera(next);
  }, [facingMode, cameraActive, recording, startCamera]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  }, []);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    setPreviewUrl(null);
    setDuration(0);
    setError(null);

    // Codec-safe: pick what this browser supports (mp4 on Safari/iPad, webm
    // elsewhere) instead of hard-coding webm, which throws on iOS.
    const { mimeType, ext } = pickRecordingType();
    let recorder;
    try {
      recorder = new MediaRecorder(streamRef.current, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: 3_500_000, // ~3.5 Mbps — sharp enough, keeps clips small
      });
    } catch (err) {
      setError(`Can't record in this browser: ${err.message}`);
      return;
    }

    const type = recorder.mimeType || mimeType || 'video/webm';
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      onRecordingComplete?.(blob, url, `recording.${ext}`);
    };

    recorder.start(100);
    mediaRecorderRef.current = recorder;
    setRecording(true);
    timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
  }, [onRecordingComplete]);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  }, []);

  useEffect(() => () => {
    stopCamera();
    clearInterval(timerRef.current);
  }, [stopCamera]);

  const formatDuration = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="flex flex-col gap-4">
      {/* Camera preview */}
      <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          playsInline
          muted
        />
        {!cameraActive && !previewUrl && (
          <div className="absolute inset-0 flex items-center justify-center text-white text-sm opacity-60">
            Camera off
          </div>
        )}
        {recording && (
          <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            REC {formatDuration(duration)}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-danger">{error}</p>}

      {/* Controls */}
      <div className="flex gap-3">
        {!cameraActive ? (
          <Button onClick={() => startCamera(facingMode)} disabled={disabled} variant="secondary" size="sm">
            Start Camera
          </Button>
        ) : (
          <>
            {!recording ? (
              <Button onClick={startRecording} variant="danger" size="sm">
                Record
              </Button>
            ) : (
              <Button onClick={stopRecording} variant="secondary" size="sm">
                Stop ({formatDuration(duration)})
              </Button>
            )}
            <Button onClick={switchCamera} disabled={recording} variant="ghost" size="sm">
              🔄 Flip ({facingMode === 'environment' ? 'Back' : 'Front'})
            </Button>
            <Button onClick={stopCamera} variant="ghost" size="sm">
              Turn Off
            </Button>
          </>
        )}
      </div>

      {/* Preview of last recording */}
      {previewUrl && (
        <div>
          <p className="text-xs text-gray-500 mb-1">Preview</p>
          <video src={previewUrl} controls playsInline muted className="w-full rounded-lg max-h-48" />
        </div>
      )}
    </div>
  );
}

export default VideoCapture;
